import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useAddepar} from '../../hooks/use-adepar';
import {useAuth} from '../../hooks/use-auth';
import {useBill} from '../../hooks/use-bill';
import {useBridge} from '../../hooks/use-bridge';
import {usePlaid} from '../../hooks/use-plaid';
import '../../styles/plaid.css';

const ClientAccounts = ({user}) => {
  const {id, type} = useParams();
  const navigate = useNavigate();

  const {
    state: {all_accounts, user_accounts, all_institutions, user_institutions},
    getUserAccounts,
    getUserInstitutions,
    removeUserInstitutions,
  } = usePlaid();
  const {
    state: {user_ids, users, users_loaded, clients_loaded},
    getClients,
    getUsers,
    deleteClient,
    deleteUser,
  } = useAuth();
  const {
    state: {user_connections, connections},
    getUserConnections,
    removeUserConnections,
  } = useBill();
  const {
    state: {
      user_connections: addepar_user_connections,
      connections: addepar_connections,
    },
    getUserConnections: getAddeparConnections,
    removeUserConnections: removeAddeparConnections,
  } = useAddepar();
  const {
    state: {
      user_connections: bridge_user_connections,
      connections: bridge_connections,
    },
    getUserConnections: getUserBridgeConnections,
    removeUserConnections: removeUserBridgeConnections,
  } = useBridge();

  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [cons, setConnections] = useState([]);
  const [addepar_cons, setAddeparCons] = useState([]);
  const [bridge_cons, setBridgeCons] = useState([]);

  useEffect(() => {
    const loadClients = async () => {
      await getClients();
    };

    const loadUsers = async () => {
      await getUsers();
    };

    if (!users_loaded) {
      loadUsers();
    }
    if (!clients_loaded) {
      loadClients();
    }
  }, []);

  useEffect(() => {
    const ids = user_ids.filter(user_id => {
      const {customerID} = users[user_id];
      if (customerID === id) {
        getUserAccounts(user_id);
        getUserConnections(user_id);
        getUserInstitutions(user_id);
        getAddeparConnections(user_id);
        getUserBridgeConnections(user_id);
        return true;
      }
      return false;
    });

    setClients(ids);
  }, [user_ids]);

  useEffect(() => {
    let sum = [];
    clients.forEach(user_id => {
      const ids = user_accounts[user_id];
      sum = [...sum, ...(ids || [])];
    });
    setAccounts(sum);
  }, [user_accounts, clients]);

  useEffect(() => {
    let sum = [];
    clients.forEach(user_id => {
      const con_ids = user_connections[user_id];
      con_ids?.forEach(con_id => {
        const connection = connections[con_id] ?? {};
        const {organizations, entity_name, accounts_payable, accounts_receivable, vendor_credits, credit_memo} = connection;
        (organizations || []).forEach(org => {
          sum.push({
            ...org,
            entity_name,
            accounts_payable,
            accounts_receivable,
            vendor_credits,
            credit_memo,
          });
        });
      });
    });
    setConnections(sum);
  }, [user_connections, clients, connections]);

  useEffect(() => {
    let sum = [];
    clients.forEach(user_id => {
      const con_ids = addepar_user_connections[user_id];
      con_ids?.forEach(con_id => {
        const {entity_mappings} = addepar_connections[con_id] ?? {};
        (entity_mappings || []).forEach(em => {
          sum.push({...em, connectionId: con_id});
        });
      });
    });
    setAddeparCons(sum);
  }, [addepar_user_connections, clients, addepar_connections]);

  useEffect(() => {
    let sum = [];
    clients.forEach(user_id => {
      const con_ids = bridge_user_connections[user_id];
      con_ids?.forEach(con_id => {
        const connection = bridge_connections[con_id] ?? {};
        const {account_mappings, custodian_mappings} = connection;
        const bankName = custodian_mappings?.[0]?.custodian;
        (account_mappings || []).forEach(am => {
          sum.push({...am, connectionId: con_id, bankName});
        });
      });
    });
    setBridgeCons(sum);
  }, [bridge_user_connections, clients, bridge_connections]);

  return (
    <div className="page-container">
      <div className="flex-row justify-between align-center">
        <p style={{fontWeight: 'bold', fontSize: '24px'}}>
          Client: {id} - {type}
        </p>
        <button
          className="gray-button"
          onClick={() => {
            navigate(-1);
          }}>
          Done
        </button>
      </div>
      <div className="flex-row justify-between">
        <table>
          {type === 'plaid' && (
            <thead>
              <tr>
                <th>Forest Entity Name</th>
                <th>Bank Name</th>
                <th>Account Name</th>
                <th>Account Type</th>
                <th>Account Number</th>
              </tr>
            </thead>
          )}
          {type === 'bill' && (
            <thead>
              <tr>
                <th>Forest Entity Name</th>
                <th>BILL Organization Name</th>
                <th>AR Journal</th>
                <th>AP Journal</th>
                <th>VC Journal</th>
                <th>CM Journal</th>
              </tr>
            </thead>
          )}
          {type === 'addepar' && (
            <thead>
              <tr>
                <th>Forest Entity Name</th>
                <th>Addepar Entity Name</th>
                <th>Addepar Entity Id</th>
                <th>Action</th>
              </tr>
            </thead>
          )}
          {type === 'bridgeft' && (
            <thead>
              <tr>
                <th>Forest Entity Name</th>
                <th>Bank Name</th>
                <th>Account Name</th>
                <th>Account Type</th>
                <th>Account Number</th>
              </tr>
            </thead>
          )}
          <tbody>
            {type === 'plaid' &&
              accounts.map(accId => {
                const acc = all_accounts[accId];
                const {
                  name,
                  subtype,
                  mask,
                  entity,
                  access_token,
                } = acc;
                const inst = all_institutions?.[access_token] ?? {};
                const {name: inst_name} = inst;

                return (
                  <tr key={accId}>
                    <td>{entity}</td>
                    <td>{inst_name}</td>
                    <td>{name}</td>
                    <td>{subtype}</td>
                    <td>{mask ? `#${mask}` : '—'}</td>
                  </tr>
                );
              })}
            {type === 'bill' &&
              cons.map((con, idx) => {
                const {
                  orgName,
                  orgId,
                  entity_name,
                  accounts_receivable,
                  accounts_payable,
                  vendor_credits,
                  credit_memo,
                } = con;

                return (
                  <tr key={`${orgId}-${idx}`}>
                    <td>{entity_name || '—'}</td>
                    <td>{orgName}</td>
                    <td>{accounts_receivable || '—'}</td>
                    <td>{accounts_payable || '—'}</td>
                    <td>{vendor_credits || '—'}</td>
                    <td>{credit_memo || '—'}</td>
                  </tr>
                );
              })}
            {type === 'addepar' &&
              addepar_cons.map((con, index) => {
                const {entity_id, entity_name, connectionId} = con;

                return (
                  <tr key={`${connectionId}-${entity_id}-${index}`}>
                    <td>{entity_name}</td>
                    <td>{entity_name}</td>
                    <td>{entity_id}</td>
                    <td>
                      <button
                        style={{color: 'blue'}}
                        onClick={() => {
                          navigate(
                            `/addepar/${connectionId}/mappings/${entity_id}`,
                          );
                        }}>
                        View Mappings
                      </button>
                    </td>
                  </tr>
                );
              })}
            {type === 'bridgeft' &&
              bridge_cons.map((con, idx) => {
                const {
                  entity,
                  acct_type,
                  custodian,
                  display_name,
                  display_number,
                  id,
                  bankName,
                } = con;

                return (
                  <tr key={`${id}-${idx}`}>
                    <td>{entity}</td>
                    <td>{custodian || bankName || '—'}</td>
                    <td>{display_name}</td>
                    <td>{acct_type}</td>
                    <td>{display_number ? `#${display_number}` : '—'}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        <div>
          <button
            className="gray-button"
            onClick={async () => {
              if (
                window.confirm('Are you sure you want to delete this client?')
              ) {
                // Proceed with delete operation
                console.log('Item deleted');

                // REMOVE CLIENT FROM CLIENT LIST
                await deleteClient(id);

                // FIND USERS
                const removed_users = user_ids.filter(user_id => {
                  const {customerID} = users?.[user_id] ?? {};
                  return customerID === id;
                });

                await Promise.all(
                  removed_users.map(async user_id => {
                    const {emailAddress} = users?.[user_id] ?? {};
                    // DELETE USER OBJECT
                    await deleteUser(user_id, emailAddress);
                    // DELETE PLAID OBJECTS
                    const access_tokens = user_institutions[user_id];
                    await removeUserInstitutions(user_id, access_tokens);
                    // DELETE BILL OBJECTS
                    const connection_ids = user_connections[user_id];
                    await removeUserConnections(user_id, connection_ids);
                    // DELETE ADDEPAR OBJECTS
                    const addepar_connection_ids =
                      addepar_user_connections[user_id];
                    await removeAddeparConnections(
                      user_id,
                      addepar_connection_ids,
                    );
                    // DELETE BRIDGEFT OBJECTS
                    const bridgeft_connection_ids =
                      bridge_user_connections[user_id] || [];
                    const bridgeft_connections_to_remove =
                      bridgeft_connection_ids.map(
                        con_id => bridge_connections[con_id],
                      );
                    await removeUserBridgeConnections(
                      user_id,
                      bridgeft_connections_to_remove,
                    );
                  }),
                );
                navigate(-1);
              } else {
                // Cancel delete operation
                console.log('Delete cancelled');
              }
            }}>
            Disconnect Accounts and Delete Client
          </button>
        </div>
      </div>
      <br />
      <br />
    </div>
  );
};

export default ClientAccounts;
